/**
 * Quick-add + mini-cart drawer (supersedes the toast, contract C3v2).
 *
 * Progressive enhancement over native form POSTs: without JS, quick-add
 * falls back to Shopify's redirect and the drawer simply never opens.
 * Every cart mutation re-fetches the cart-drawer SECTION via the
 * Section Rendering API, so all money stays server-formatted — this
 * file renders no prices, ever.
 *
 * The drawer's primary CTA links to the CART page, never to checkout:
 * checkout would bypass the Wunschtag picker (same rationale as D10).
 */
(() => {
  if (window.__bunzQuickAdd) return;
  window.__bunzQuickAdd = true;

  const drawer = () => document.querySelector('[data-cart-drawer]');
  const overlay = () => document.querySelector('.cart-drawer__overlay');
  let lastFocus = null;
  let busy = false;

  const syncBadge = () => {
    const inner = document.getElementById('CartDrawerInner');
    const count = Number(inner?.dataset.cartCountValue ?? 0);
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  };

  const refresh = async () => {
    // Shopify.routes.root is "/en/" on the English storefront. A bare "/"
    // re-rendered the drawer in German, and its "Zum Warenkorb" link then
    // sent English shoppers to the German cart and a German checkout.
    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root}?section_id=cart-drawer`);
    if (!res.ok) return false;
    const html = await res.text();
    // trust boundary: same-origin Section Rendering API — our own
    // section, all fields Liquid-escaped server-side
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    const fresh = tpl.content.querySelector('#CartDrawerInner');
    const mounted = document.getElementById('CartDrawerInner');
    if (fresh && mounted) mounted.replaceWith(fresh);
    syncBadge();
    return true;
  };

  const open = () => {
    const d = drawer();
    if (!d) return;
    lastFocus = document.activeElement;
    d.hidden = false;
    overlay().hidden = false;
    document.documentElement.style.overflow = 'hidden';
    d.querySelector('.cart-drawer__close')?.focus();
  };

  const close = () => {
    const d = drawer();
    if (!d || d.hidden) return;
    d.hidden = true;
    overlay().hidden = true;
    document.documentElement.style.overflow = '';
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
  };

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-quick-add]');
    if (!form) return;
    e.preventDefault();
    if (busy) return;
    busy = true;
    fetch(form.action + '.js', { method: 'POST', body: new FormData(form) })
      .then(async (res) => {
        if (!res.ok) throw new Error('add failed');
        await refresh();
        open();
      })
      .catch(() => form.submit())
      .finally(() => { busy = false; });
  });

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-drawer-close]')) {
      close();
      return;
    }
    const change = e.target.closest('[data-drawer-line-change]');
    if (change && !busy) {
      busy = true;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: Number(change.dataset.line), quantity: Number(change.dataset.quantity) }),
      })
        .then((res) => { if (res.ok) return refresh(); })
        .catch(() => {})
        .finally(() => { busy = false; });
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });

  // keep the badge honest on plain page loads too
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncBadge);
  } else {
    syncBadge();
  }
})();
